import type { ComponentType } from "react";
import TermsV2026_05_20, {
  lastUpdatedLabel as termsLabel_2026_05_20,
} from "./2026-05-20/terms";
import PrivacyV2026_05_20, {
  lastUpdatedLabel as privacyLabel_2026_05_20,
} from "./2026-05-20/privacy";
import DpaV2026_05_20, {
  lastUpdatedLabel as dpaLabel_2026_05_20,
} from "./2026-05-20/dpa";
import ConnectIdTermsV2026_05_20, {
  lastUpdatedLabel as connectIdTermsLabel_2026_05_20,
} from "./2026-05-20/connectid-terms";
import TermsV2026_08_12, {
  lastUpdatedLabel as termsLabel_2026_08_12,
} from "./2026-08-12/terms";
import PrivacyV2026_08_12, {
  lastUpdatedLabel as privacyLabel_2026_08_12,
} from "./2026-08-12/privacy";
import DpaV2026_08_12, {
  lastUpdatedLabel as dpaLabel_2026_08_12,
} from "./2026-08-12/dpa";
import ConnectIdTermsV2026_08_12, {
  lastUpdatedLabel as connectIdTermsLabel_2026_08_12,
} from "./2026-08-12/connectid-terms";

export interface LegalVersion {
  version: string;
  lastUpdatedLabel: string;
  Terms: ComponentType;
  Privacy: ComponentType;
  Dpa: ComponentType;
  ConnectIdTerms: ComponentType;
}

/**
 * Archive of every Terms / Privacy Policy / DPA version we have ever
 * published. The key MUST match the `TERMS_VERSION` constant in
 * `artifacts/api-server/src/routes/verify.ts` and the
 * `CURRENT_TERMS_VERSION` / `CURRENT_DPA_VERSION` constants in
 * `lib/db/src/schema/terms-acceptances.ts` for the same point in time, so
 * the version string stored against each consent / acceptance record
 * resolves to the exact copy the user agreed to.
 *
 * To publish a new version, see `./README.md`.
 */
export const LEGAL_VERSIONS: Record<string, LegalVersion> = {
  "2026-08-12": {
    version: "2026-08-12",
    lastUpdatedLabel: termsLabel_2026_08_12,
    Terms: TermsV2026_08_12,
    Privacy: PrivacyV2026_08_12,
    Dpa: DpaV2026_08_12,
    ConnectIdTerms: ConnectIdTermsV2026_08_12,
  },
  "2026-05-20": {
    version: "2026-05-20",
    lastUpdatedLabel: termsLabel_2026_05_20,
    Terms: TermsV2026_05_20,
    Privacy: PrivacyV2026_05_20,
    Dpa: DpaV2026_05_20,
    ConnectIdTerms: ConnectIdTermsV2026_05_20,
  },
};

// Sanity check: terms, privacy, DPA, and ConnectID-terms labels for a given
// version are kept in sync at the file level. We surface a single label per
// version.
void privacyLabel_2026_05_20;
void dpaLabel_2026_05_20;
void connectIdTermsLabel_2026_05_20;
void privacyLabel_2026_08_12;
void dpaLabel_2026_08_12;
void connectIdTermsLabel_2026_08_12;

/**
 * The currently published version. Must match `TERMS_VERSION` in the API
 * and `CURRENT_TERMS_VERSION` / `CURRENT_DPA_VERSION` in the DB schema.
 */
export const CURRENT_LEGAL_VERSION = "2026-08-12";

/**
 * The currently published ConnectID Minimum Customer Terms version. Versioned
 * in parallel with — but independently of — the Terms/Privacy/DPA above. Must
 * match `CURRENT_CONNECTID_TERMS_VERSION` in
 * `lib/db/src/schema/connectid-terms-acceptances.ts`. The string MUST resolve
 * to a `LEGAL_VERSIONS` entry (which supplies the `ConnectIdTerms` component).
 */
export const CURRENT_CONNECTID_TERMS_VERSION = "2026-05-20";

export function getLegalVersion(requested: string | null | undefined): {
  entry: LegalVersion;
  isCurrent: boolean;
  isKnown: boolean;
} {
  const current = LEGAL_VERSIONS[CURRENT_LEGAL_VERSION]!;
  if (!requested) return { entry: current, isCurrent: true, isKnown: true };
  const match = LEGAL_VERSIONS[requested];
  if (!match) return { entry: current, isCurrent: true, isKnown: false };
  return {
    entry: match,
    isCurrent: match.version === CURRENT_LEGAL_VERSION,
    isKnown: true,
  };
}

/**
 * Resolve a ConnectID Minimum Customer Terms version. Mirrors
 * `getLegalVersion` but defaults to and compares against
 * `CURRENT_CONNECTID_TERMS_VERSION` so the ConnectID terms can be bumped on
 * its own cadence.
 */
export function getConnectIdTermsVersion(
  requested: string | null | undefined,
): {
  entry: LegalVersion;
  isCurrent: boolean;
  isKnown: boolean;
} {
  const current = LEGAL_VERSIONS[CURRENT_CONNECTID_TERMS_VERSION]!;
  if (!requested) return { entry: current, isCurrent: true, isKnown: true };
  const match = LEGAL_VERSIONS[requested];
  if (!match) return { entry: current, isCurrent: true, isKnown: false };
  return {
    entry: match,
    isCurrent: match.version === CURRENT_CONNECTID_TERMS_VERSION,
    isKnown: true,
  };
}

export function listLegalVersions(): LegalVersion[] {
  return Object.values(LEGAL_VERSIONS).sort((a, b) =>
    a.version < b.version ? 1 : -1,
  );
}

/**
 * Versions that carry a published ConnectID Minimum Customer Terms document,
 * newest first. Currently every archived version includes one.
 */
export function listConnectIdTermsVersions(): LegalVersion[] {
  return listLegalVersions();
}
